import { type Certificate, type CertificateType } from '@/api';

interface CertificatePreviewProps {
  certificate: Certificate;
  onDownload?: () => void;
  onPrint?: () => void;
  disabled?: boolean;
}

const certificateTypeLabels: Record<CertificateType, string> = {
  SICK_NOTE: 'Atestado de Doença',
  HEALTH_CERTIFICATE: 'Atestado de Saúde',
  TRAVEL_CERTIFICATE: 'Atestado de Viagem',
  OTHER: 'Outro Atestado',
};

export function CertificatePreview({
  certificate,
  onDownload,
  onPrint,
  disabled = false,
}: CertificatePreviewProps) {
  const formatDate = (dateString: string): string => {
    return new Date(dateString).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: 'long',
      year: 'numeric',
    });
  };

  const isValid = certificate.signedAt && certificate.digitalSignature;

  return (
    <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
      <div className="px-4 py-3 bg-gray-50 border-b border-gray-200 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <svg className="w-5 h-5 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
          </svg>
          <span className="font-medium text-gray-900">
            {certificateTypeLabels[certificate.type] || certificate.type}
          </span>
        </div>
        {isValid ? (
          <span className="px-2 py-0.5 bg-green-100 text-green-700 rounded text-xs font-medium">
            Assinado Digitalmente
          </span>
        ) : (
          <span className="px-2 py-0.5 bg-amber-100 text-amber-700 rounded text-xs font-medium">
            Pendente de Assinatura
          </span>
        )}
      </div>

      <div className="p-4 space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <p className="text-xs text-gray-500 uppercase tracking-wide">Período</p>
            <p className="text-sm font-medium text-gray-900 mt-0.5">
              {formatDate(certificate.startDate)}
              {certificate.endDate && ` até ${formatDate(certificate.endDate)}`}
            </p>
          </div>
          {certificate.daysOff && (
            <div>
              <p className="text-xs text-gray-500 uppercase tracking-wide">Dias de Afastamento</p>
              <p className="text-sm font-medium text-gray-900 mt-0.5">
                {certificate.daysOff} dia(s)
              </p>
            </div>
          )}
        </div>

        <div>
          <p className="text-xs text-gray-500 uppercase tracking-wide">Motivo</p>
          <p className="text-sm text-gray-900 mt-0.5">{certificate.reason}</p>
        </div>

        {certificate.observations && (
          <div>
            <p className="text-xs text-gray-500 uppercase tracking-wide">Observações</p>
            <p className="text-sm text-gray-600 mt-0.5">{certificate.observations}</p>
          </div>
        )}

        {certificate.signedAt && (
          <div className="pt-3 border-t border-gray-200">
            <p className="text-xs text-gray-500">Assinado em</p>
            <p className="text-sm text-gray-900 mt-0.5">
              {formatDate(certificate.signedAt)}
            </p>
            {certificate.digitalSignature && (
              <p className="text-xs text-gray-400 mt-1 font-mono truncate">
                {certificate.digitalSignature}
              </p>
            )}
          </div>
        )}
      </div>

      {(onDownload || onPrint) && (
        <div className="px-4 py-3 bg-gray-50 border-t border-gray-200 flex gap-2">
          {onDownload && (
            <button
              onClick={onDownload}
              disabled={disabled}
              className="flex items-center gap-1 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-100 rounded disabled:opacity-50"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
              </svg>
              Baixar PDF
            </button>
          )}
          {onPrint && (
            <button
              onClick={onPrint}
              disabled={disabled}
              className="flex items-center gap-1 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-100 rounded disabled:opacity-50"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
              </svg>
              Imprimir
            </button>
          )}
        </div>
      )}
    </div>
  );
}
